import { useContext, useState } from 'react'

import { Box, Button, Tooltip, Typography } from '@mui/material'
import { IconInfoCircle, IconSparkles } from '@tabler/icons-react'
import type { CRMObject } from 'types/graphql'

import ObjectTile from 'src/components/ObjectTile/ObjectTile'
import Row from 'src/components/Row/Row'
import { DayContext } from 'src/lib/dayContext'

const INITIAL_TILE_LIMIT = 3

const containerSx = {
  px: '12px',
  gap: 2,
  alignItems: 'flex-start',
  '& .thread-message-tool-icon': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px !important',
    height: '48px !important',
    backgroundColor: (theme) => theme.palette.divider,
    borderRadius: '8px',
    flexShrink: 0,
    mt: 0.5,
  },

  '& .thread-message-tool-content': {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    width: '100%',
  },

  '& .thread-message-tool-label-row': {
    alignItems: 'center',
    gap: 0.5,
  },

  '& .thread-message-tool-label': {
    fontWeight: 600,
  },

  '& .thread-message-context-result-tiles-container': {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 2,
    width: '100%',
    alignItems: 'start',
  },

  '& .thread-message-show-more-button': {
    minWidth: 'auto',
    padding: '2px 8px',
    height: 'auto',
    lineHeight: 1.5,
    gridColumn: '1 / -1',
    justifySelf: 'start',
  },
}

const ThreadContextResult = ({
  result,
  threadMessageId,
}: {
  result: { objects: CRMObject[] }
  threadMessageId: string
}) => {
  const [showAll, setShowAll] = useState(false)
  const { setSidebarObject, selectedWorkspace } = useContext(DayContext)

  const totalObjects = result.objects.length
  const displayedObjects = showAll
    ? result.objects
    : result.objects.slice(0, INITIAL_TILE_LIMIT)
  const remainingObjects = totalObjects - INITIAL_TILE_LIMIT

  return (
    <Row sx={containerSx}>
      <Box className="thread-message-tool-icon">
        <IconSparkles size={26} />
      </Box>
      <Box className="thread-message-tool-content">
        <Row className="thread-message-tool-label-row">
          <IconSparkles size={12} />
          {totalObjects > 0 ? (
            <>
              <Typography className="thread-message-tool-label">
                Found {totalObjects} object{totalObjects === 1 ? '' : 's'} for
                context
              </Typography>
              <Tooltip
                arrow
                placement="top"
                title="Relevant objects found based on your message can improve AI responses."
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    ml: 0.5,
                    color: 'text.secondary',
                  }}
                >
                  <IconInfoCircle size={14} />
                </Box>
              </Tooltip>
            </>
          ) : (
            <Typography className="thread-message-tool-label">
              No relevant objects found in context
            </Typography>
          )}
        </Row>

        {totalObjects > 0 && (
          <Box className="thread-message-context-result-tiles-container">
            {displayedObjects.map((object) => (
              <ObjectTile
                key={`context-result-object-${object.objectId}-${threadMessageId}`}
                workspaceId={object.workspaceId}
                objectType={object.objectType}
                objectId={object.objectId}
                onClick={() => {
                  setSidebarObject({
                    objectType: object.objectType,
                    objectId: object.objectId,
                    workspaceId: selectedWorkspace,
                  })
                }}
              />
            ))}
            {totalObjects > INITIAL_TILE_LIMIT && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowAll(!showAll)}
                className="thread-message-show-more-button"
              >
                {showAll ? 'Show Less' : `+${remainingObjects} more`}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Row>
  )
}

export default ThreadContextResult
